import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  AppCarousel,
  Col,
  Row,
  UiBox,
  UiButton,
  UiDivider,
  UiImage,
  UiListItem,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiUnorderedList,
} from '~/components/elements';
import BoldText from '~/components/shared/BoldText';
import {PAGE_URLS, QUERY_MOBILE, QUERY_SMALL_MOBILE} from '~/constants';
import {
  PRODUCT_SKUS,
  WEIGHT_LOSS_KEYS,
  WEIGHT_LOSS_ONE_QUESTIONNAIRES,
  WL_PROGRAMS,
} from '~/constants/medical-questionnaires';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import saxenda from '~/assets/images/saxenda.png';
import atomeLogo from '~/assets/images/atome-logo.png';
import {useInput} from '~/hooks';
import {useMediaQuery} from '@chakra-ui/react';
import {MedProduct} from '~/types/models/questionnaire';
import {UiCard} from '~/components/elements/UiCard';
import rybelsusCensored from '~/assets/images/rybelsus-censored.png';
import {getCommaNumber} from '~/utils/helpers';

interface Props {}

const QuestionnaireRybelsusPackageScreen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;

  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);
  const [isSmallMobile] = useMediaQuery(`(max-width: ${QUERY_SMALL_MOBILE})`);
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_ONE_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_KEYS.RYBELSUS_PACKAGE_SELECTION);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_KEYS.RYBELSUS_PACKAGE_SELECTION]?.data,
  );

  const selectedProgram = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_KEYS.PROGRAM_SELECTION],
  );

  const collectedData = mdq.collectedData;

  const defaultVariant = questionnaire?.options?.find(i => i.defaultSelection);

  const defaultProduct = defaultVariant?.data.find((i: any) => i.isPopular);

  const isDoctorDecide = currentAnswer?.productSku === PRODUCT_SKUS.DOCTOR_TEL;

  const {inputs, setInputs, validation} = useInput({
    productSku: (currentAnswer?.productSku && !isDoctorDecide ? currentAnswer?.productSku : defaultProduct?.sku) || '',
    // doctorDecide: currentAnswer?.productSku === PRODUCT_SKUS.DOCTOR_TEL,
    selectedVariant: currentAnswer?.selectedVariant || defaultVariant?.value || '',
  });

  const carouselData = questionnaire?.options?.find(val => val.value === inputs.selectedVariant)?.data;

  const selectedItemIndex =
    carouselData?.findIndex((i: any) => {
      return inputs.productSku ? i.sku === inputs.productSku : i.isPopular;
    }) || 0;

  const onChangeCarousel = (pos: number) => {
    setInputs({
      productSku: carouselData?.[pos]?.sku || '',
      // doctorDecide: false,
    });
  };

  const onClickDoctorDecide = () => {
    onClickSubmit(true);
  };

  const onClickSubmit = (doctorDecide?: boolean) => {
    const selectedSku = doctorDecide ? PRODUCT_SKUS.DOCTOR_TEL : inputs.productSku;
    const answerData = {
      key: WEIGHT_LOSS_KEYS.RYBELSUS_PACKAGE_SELECTION,
      index: questionnaire?.index || '',
      data: {
        productSku: selectedSku,
        selectedVariant: inputs.selectedVariant,
      },
      order: answerOrder + 1,
    };

    const selectedPackage = {
      key: WEIGHT_LOSS_KEYS.PACKAGE_SELECTION,
      index: questionnaire?.index || '',
      data: {
        productSkus: [selectedSku, PRODUCT_SKUS.TEL_PILL],
      },
      order: answerOrder + 2,
    };

    let newSelectedProgram;

    if (selectedProgram?.data?.selectedProgram === WL_PROGRAMS.LET_DOCTOR_DECIDE.value && !doctorDecide) {
      newSelectedProgram = {
        ...selectedProgram,
        key: WEIGHT_LOSS_KEYS.PROGRAM_SELECTION,
        data: {
          selectedProgram: WL_PROGRAMS.ELEVATE_RYBELSUS.value,
          selectedProgramLocal: WL_PROGRAMS.ELEVATE_RYBELSUS.localValue,
        },
      };
      dispatch(setQuestionnaireAnswer(newSelectedProgram));
    }

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(setQuestionnaireAnswer(selectedPackage));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_KEYS.RYBELSUS_PACKAGE_SELECTION]: answerData,
            [WEIGHT_LOSS_KEYS.PACKAGE_SELECTION]: selectedPackage,
            ...(newSelectedProgram ? {[WEIGHT_LOSS_KEYS.PROGRAM_SELECTION]: newSelectedProgram} : {}),
          },
        },
      }),
    );
    if (doctorDecide) {
      history.push(PAGE_URLS.MDQ1_DOCTOR_CONSULTATION);
      return;
    }
    history.push(PAGE_URLS.MDQ1_WHATS_NEXT_RYBELSUS);
  };

  const getPriceString = (price: number) => {
    return getCommaNumber(price);
  };

  const renderCarouselItem = (products: MedProduct[]) =>
    products?.map((i, index) => {
      const isSelected = inputs.productSku === i?.sku;
      return (
        <Col
          key={index}
          minWidth="30%"
          mx={{base: 2, md: 4}}
          border="1px solid"
          borderRadius="2xl"
          transition="300ms"
          _hover={{cursor: 'pointer'}}
          alignItems="center"
          justifyContent="center"
          pt={6}
          pb={4}
          px={{base: 2, lg: 3}}
          bg={i.isPopular ? 'linear-gradient(180deg, #E6F1B4 0%, #EBFFFD 100%);' : undefined}
          boxShadow={isSelected ? '0px 0px 0px 4px #CCDF75' : 'none'}
        >
          {i.isPopular && (
            <Col w="100%" position="absolute" justifyContent="start" alignItems="center" top={-4}>
              <Col bg="lightBaseColor" key={index} px={4} py={1.5} borderRadius="100px">
                <UiText fontSize="sm" fontWeight="600">
                  Most Popular
                </UiText>
              </Col>
            </Col>
          )}

          <UiText fontSize="xl" fontWeight="600">
            {i?.period}
          </UiText>
          <Row alignItems="center">
            <UiText fontSize="2xl" fontWeight="500">
              ${getPriceString(i?.price)}
            </UiText>
            <UiText fontSize="sm" fontWeight="500" mt={2}>
              &nbsp;{i?.priceSuffix}
            </UiText>
          </Row>
          <UiBox minH={4} my={3.5}>
            {i.payLaterPrice && (
              <Row alignItems="flex-end" lineHeight="15px">
                {i.canPayLater && (
                  <UiText fontSize="0.625rem" fontWeight="500">
                    or&nbsp;
                  </UiText>
                )}
                <UiText fontSize="sm" fontWeight="700">
                  ${i.payLaterPrice}
                </UiText>
                <UiText fontSize="0.625rem" fontWeight="500">
                  &nbsp;/{i.periodSuffix}
                </UiText>
              </Row>
            )}
          </UiBox>
          <UiText fontSize="0.625rem" fontWeight="400" px={2}>
            {i.description}
          </UiText>
          <UiBox minH={9}>
            {i.canPayLater && (
              <img src={atomeLogo} alt="atome" style={{width: '6rem', height: '2rem', objectFit: 'contain'}} />
            )}
          </UiBox>
        </Col>
      );
    });

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      checkoutStep={questionnaire?.checkoutStep}
      handleOnNext={onClickSubmit}
      pageTitle={questionnaire?.title}
      disableNext={!inputs?.productSku || !inputs?.selectedVariant}
    >
      <Col alignItems="center" textAlign="center" mt={12} px={2}>
        <UiText fontSize="xl" fontWeight="bold" mb={2}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mb={2}>
          <img src={rybelsusCensored} alt="Rybelsus" style={{width: '8rem', height: '5rem', objectFit: 'contain'}} />
        </UiBox>
        <UiText fontSize="10px" fontWeight="400" mb={2}>
          {questionnaire?.questions?.sub}
        </UiText>
        <Row w="100%">
          {questionnaire?.options?.map((i, index) => (
            <UiButton
              key={i.key}
              title={i.label}
              variant="outline"
              onClick={() =>
                setInputs({
                  selectedVariant: i.value,
                  productSku: i.data.find((item: any) => item.isPopular)?.sku,
                })
              }
              whiteSpace="pre-wrap"
              color="trueBlack"
              borderColor="trueBlack"
              background={inputs.selectedVariant === i.value ? 'lightBaseColor' : 'white'}
              fontWeight={500}
              borderRadius={48}
              minH={9}
              mr="3%"
              minW="32%"
              _hover={{}}
            />
          ))}
        </Row>
        <UiBox w="100%" className="pack-size" mt={4}>
          <AppCarousel
            selectedItem={selectedItemIndex >= 0 ? selectedItemIndex : 0}
            onChange={onChangeCarousel}
            onClickItem={index => onChangeCarousel(index)}
            showIndicators={false}
            showArrows={false}
            showStatus={false}
            showThumbs={false}
            centerMode
            centerSlidePercentage={isDesktop ? 50 : isSmallMobile ? 65 : 48}
            emulateTouch
            preventMovementUntilSwipeScrollTolerance={true}
            swipeScrollTolerance={50}
          >
            {renderCarouselItem(carouselData || [])}
          </AppCarousel>
        </UiBox>
        <Col w="60%" alignSelf="center" mb={6}>
          <UiText fontSize="sm" color="liteOuter" mb={4}>
            Not sure what to choose?
          </UiText>
          <UiButton
            // background={inputs.doctorDecide ? 'lightBaseColor' : '#F2EEE4'}
            background="#F2EEE4"
            _hover={{}}
            textColor="trueBlack"
            onClick={onClickDoctorDecide}
          >
            Let the doctor decide
          </UiButton>
        </Col>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default QuestionnaireRybelsusPackageScreen;
